// Şifremi Unuttum — plan §4.1.2 + design ref #screen-forgot.
// İki state: form (input) ve success (email gönderildi).

import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { AuthShell } from "../components/auth/AuthShell";
import { forgotPassword } from "../api/auth";
import { ApiError } from "../api/client";
import { rateLimitMessage } from "../lib/auth-rate-limit";

export function Forgot(): JSX.Element {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email || loading) return;
    setLoading(true);
    setError(null);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      const limited = rateLimitMessage(err);
      if (limited) {
        setError(limited);
      } else if (err instanceof ApiError && err.status === 422) {
        setError("Geçerli bir email adresi girin.");
      } else {
        setError(err instanceof Error ? err.message : "Beklenmedik bir hata oluştu.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell pitch="Şifrenizi unutursanız bile, raporlarınız bizimle güvende.">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm mb-5 hover:underline"
        style={{ color: "var(--color-primary)" }}
      >
        <span aria-hidden="true">←</span> Giriş ekranına dön
      </Link>

      {sent ? (
        <SuccessPanel email={email} />
      ) : (
        <form onSubmit={(e) => void onSubmit(e)}>
          <h2
            className="text-3xl mb-2"
            style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
          >
            Şifrenizi mi unuttunuz?
          </h2>
          <p className="mb-8" style={{ color: "var(--color-text-2)" }}>
            Hesabınızla ilişkili e-posta adresini girin. Size sıfırlama linki göndereceğiz.
          </p>

          <label className="block mb-6">
            <span
              className="block mb-1.5 text-sm font-medium"
              style={{ color: "var(--color-text)" }}
            >
              E-posta
            </span>
            <input
              type="email"
              autoComplete="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              className="w-full px-3 py-2.5 rounded-lg border"
              style={{
                background: "var(--color-surface)",
                borderColor: "var(--color-border)",
                color: "var(--color-text)",
                fontSize: "0.95rem",
              }}
              placeholder="ornek@danismanlik.com"
            />
          </label>

          {error ? (
            <div
              role="alert"
              data-alert-variant="error"
              className="mb-4 px-3 py-2 rounded-lg text-sm"
              style={{
                background: "color-mix(in srgb, var(--color-error) 10%, white)",
                color: "var(--color-error)",
                border: "1px solid color-mix(in srgb, var(--color-error) 35%, white)",
              }}
            >
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={loading || !email}
            className="w-full px-4 py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: "var(--color-primary)",
              color: "var(--color-surface)",
              fontSize: "0.95rem",
            }}
          >
            {loading ? "Gönderiliyor…" : "Sıfırlama Linki Gönder"}
          </button>

          <div
            className="mt-5 px-3 py-2 rounded-lg text-xs flex items-start gap-2"
            style={{
              background: "rgba(15,42,61,0.04)",
              color: "var(--color-text-2)",
              border: "1px solid var(--color-border)",
            }}
          >
            <span aria-hidden="true">ⓘ</span>
            <span>
              Sıfırlama linki <strong>5 dakika</strong> geçerlidir ve sadece bir kez kullanılabilir.
            </span>
          </div>
        </form>
      )}
    </AuthShell>
  );
}

function SuccessPanel({ email }: { email: string }): JSX.Element {
  return (
    <div>
      <div
        className="mb-4 flex items-center justify-center rounded-full"
        style={{
          width: 64,
          height: 64,
          background: "color-mix(in srgb, var(--color-success) 12%, white)",
          color: "var(--color-success)",
          fontSize: "2rem",
        }}
        aria-hidden="true"
      >
        ✓
      </div>
      <h2
        className="text-2xl mb-3"
        style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
      >
        Kontrolünüz için
      </h2>
      <p className="mb-4" style={{ color: "var(--color-text-2)" }}>
        Eğer <strong style={{ color: "var(--color-text)" }}>{email}</strong> adresi sistemde
        kayıtlıysa, sıfırlama linkini e-postanıza gönderdik. Lütfen gelen kutunuzu (ve spam
        klasörünü) kontrol edin.
      </p>
      <p className="mb-6 text-sm" style={{ color: "var(--color-text-3)" }}>
        Link 5 dakika geçerli. Gelmediyse tekrar deneyebilirsiniz.
      </p>
      <Link
        to="/"
        className="inline-block px-4 py-2.5 rounded-lg font-medium"
        style={{
          background: "transparent",
          border: "1px solid var(--color-border)",
          color: "var(--color-text)",
          fontSize: "0.9rem",
        }}
      >
        Giriş ekranına dön
      </Link>
    </div>
  );
}
